import { ChartData } from "chart.js";
import { useMemo } from "react";
import { Bar } from "react-chartjs-2";

const DEFAULT_BAR_COLOR = "#6366f1";

interface BarChartData {
  value: number;
  backgroundColor?: string;
  label?: string;
  [key: string]: unknown;
}

export interface BarChartProps {
  label?: string;
  data?: BarChartData[];
  extraAccessorKey?: string;
  extraAccessorLabel?: string;
  min?: number;
  max?: number;
  accessorLabel?: string;
  suffix?: string;
}

export function BarChart({
  label,
  data,
  extraAccessorKey,
  extraAccessorLabel,
  min,
  max,
  accessorLabel,
  suffix,
}: BarChartProps) {
  const barData: ChartData<"bar", number[], string> = useMemo(
    () => ({
      labels: label ? [label] : undefined,
      datasets:
        data?.map((dataItem) => ({
          ...dataItem,
          label: dataItem.label,
          data: [dataItem.value],
          backgroundColor: dataItem.backgroundColor || DEFAULT_BAR_COLOR,
        })) || [],
    }),
    [data, label]
  );

  const barOptions = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: {
          display: false,
          position: "top" as const,
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const value = context.raw;

              const extraDefinition =
                extraAccessorKey && extraAccessorLabel && data
                  ? `${extraAccessorLabel}: ${
                      data?.[context.datasetIndex][extraAccessorKey]
                    }`
                  : "";

              return [
                `${accessorLabel || "Perc"}: ${value}${suffix || ""}`,
                ...(extraDefinition && [extraDefinition]),
              ];
            },
          },
          displayColors: false,
        },
      },
      scales: {
        y: {
          beginAtZero: true, // Start y-axis at 0
          suggestedMin: min,
          suggestedMax: max,
        },
      },
    }),
    [
      data,
      extraAccessorKey,
      extraAccessorLabel,
      min,
      max,
      accessorLabel,
      suffix,
    ]
  );

  return (
    <div>
      <Bar options={barOptions} data={barData} />
    </div>
  );
}
